import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { MqttService, MqttServiceConfig } from 'ngx-mqtt';
import { Observable, Subscription, throwError } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DeliveryTrack, isTerminalState, sanitizeTrack } from './delivery-tracking.models';

/** Contract: clients poll the track endpoint every 15 s. */
export const TRACK_POLL_INTERVAL_MS = 15000;
/** A slow first answer must not hold up the order status page. */
export const TRACK_REQUEST_TIMEOUT_MS = 8000;

/**
 * Client for the public customer tracking endpoint.
 *
 * `watch()` emits the latest track, or `null` exactly once when tracking is not
 * available for this order (404, backend not deployed, network error on the
 * first request) so the caller can fall back to the classic order progress.
 *
 * Positions are only ever held in memory; nothing is written to storage.
 */
@Injectable({ providedIn: 'root' })
export class DeliveryTrackingService {
  private readonly endpoint = environment.orderApiBackend;

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private injector: Injector
  ) {}

  /**
   * The app-wide MQTT client, or null when the app has none configured. It is only
   * looked up once tracking is confirmed for an order, so this service never opens
   * a broker connection of its own and never touches MQTT on pages without tracking.
   */
  private mqttClient(): MqttService | null {
    try {
      if (!this.injector.get(MqttServiceConfig, null)) {
        return null;
      }
      return this.injector.get(MqttService, null);
    } catch (e) {
      return null;
    }
  }

  trackUrl(orderHash: string): string {
    return `${this.endpoint}/backend/v2/delivery/track/${encodeURIComponent(orderHash)}/`;
  }

  topic(orderHash: string): string {
    return `/delivery/${orderHash}/track`;
  }

  getTrack(orderHash: string): Observable<DeliveryTrack> {
    if (!orderHash) {
      return throwError(() => new Error('Missing order hash'));
    }
    // Background polling must not flash the page-level loading bar every 15 s.
    const context = new HttpContext().set(NGX_LOADING_BAR_IGNORED, true);
    return this.http.get<unknown>(this.trackUrl(orderHash), { context }).pipe(
      timeout(TRACK_REQUEST_TIMEOUT_MS),
      map((raw) => {
        const track = sanitizeTrack(raw);
        if (!track) {
          throw new Error('Unexpected track response');
        }
        return track;
      })
    );
  }

  watch(orderHash: string, pollMs: number = TRACK_POLL_INTERVAL_MS): Observable<DeliveryTrack | null> {
    return new Observable<DeliveryTrack | null>((subscriber) => {
      const doc = this.document;
      let timer: any = null;
      let request: Subscription | null = null;
      let mqttSub: Subscription | null = null;
      let established = false;
      let finished = false;

      const hidden = () => doc.visibilityState === 'hidden';

      const clearTimer = () => {
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
      };

      const teardown = () => {
        finished = true;
        clearTimer();
        if (request) {
          request.unsubscribe();
          request = null;
        }
        if (mqttSub) {
          mqttSub.unsubscribe();
          mqttSub = null;
        }
        doc.removeEventListener('visibilitychange', onVisibility);
      };

      const schedule = () => {
        clearTimer();
        if (finished || hidden()) {
          return;
        }
        timer = setTimeout(poll, pollMs);
      };

      const accept = (track: DeliveryTrack) => {
        if (!established) {
          established = true;
          listenToMqtt();
        }
        subscriber.next(track);
        if (isTerminalState(track.state)) {
          teardown();
          subscriber.complete();
        }
      };

      const poll = () => {
        clearTimer();
        if (finished || request) {
          return;
        }
        request = this.getTrack(orderHash).subscribe({
          next: (track) => {
            request = null;
            accept(track);
            schedule();
          },
          error: () => {
            request = null;
            if (!established) {
              // Tracking is not available for this order: hand back to the classic page.
              subscriber.next(null);
              teardown();
              subscriber.complete();
              return;
            }
            // Transient failure after a good start: keep what we have, try again later.
            schedule();
          },
        });
      };

      const listenToMqtt = () => {
        if (mqttSub || finished) {
          return;
        }
        const mqtt = this.mqttClient();
        if (!mqtt) {
          return;
        }
        try {
          mqttSub = mqtt.observe(this.topic(orderHash)).subscribe({
            next: (message) => {
              if (finished || hidden()) {
                return;
              }
              let track: DeliveryTrack | null = null;
              try {
                track = sanitizeTrack(JSON.parse(message.payload.toString()));
              } catch (e) {
                track = null;
              }
              if (track) {
                accept(track);
                schedule();
              } else {
                // Unreadable nudge: ask the endpoint instead.
                poll();
              }
            },
            error: () => {
              mqttSub = null; // polling carries on without the broker
            },
          });
        } catch (e) {
          mqttSub = null;
        }
      };

      const onVisibility = () => {
        if (finished) {
          return;
        }
        if (hidden()) {
          clearTimer();
        } else if (established) {
          poll(); // catch up straight away, then resume the interval
        }
      };

      doc.addEventListener('visibilitychange', onVisibility);
      poll();

      return teardown;
    });
  }
}
